import maxBy from 'lodash/maxBy';
import orderBy from 'lodash/orderBy';
import { z } from 'zod/v4';

// Durable report-owned V1 wire contract. Do not compose this from live API/detail schemas.
const PriceBreakV1Schema = z.object({ quantity: z.number().nonnegative(), unitPrice: z.number().positive() });
const OfferV1Schema = z
    .object({
        id: z.string().min(1),
        linkedIpn: z.string().min(1).optional(),
        partId: z.string().min(1).optional(),
        observedAt: z.iso.datetime({ offset: true }).nullable(),
        label: z.string().optional(),
        priceBreaks: z.array(PriceBreakV1Schema),
        supplier: z
            .object({ id: z.string().min(1), name: z.string().min(1), stockLocation: z.string().optional() })
            .optional(),
        stock: z.number().nonnegative().optional(),
        leadTimeDays: z.number().nonnegative().optional(),
    })
    .refine((offer) => (offer.linkedIpn !== undefined) !== (offer.partId !== undefined), {
        message: 'Exactly one owner, linkedIpn or partId, is required',
    });
const SelectionV1Schema = z.object({
    offerId: z.string().min(1),
    quantity: z.number().nonnegative(),
    unitPrice: z.number().positive(),
});

export const MarketSavingsStandardPriceSnapshotV1Schema = z
    .object({
        version: z.literal(1),
        ipn: z.string().min(1),
        capturedAt: z.iso.datetime({ offset: true }),
        currency: z.string().min(1),
        offers: z.array(OfferV1Schema),
        selectionsByBreak: z.object({
            '1': SelectionV1Schema.nullable(),
            '100': SelectionV1Schema.nullable(),
            '1k': SelectionV1Schema.nullable(),
            '10k': SelectionV1Schema.nullable(),
            '50k': SelectionV1Schema.nullable(),
        }),
    })
    .superRefine((snapshot, ctx) => {
        for (const [index, offer] of snapshot.offers.entries()) {
            if (offer.linkedIpn !== undefined && offer.linkedIpn !== snapshot.ipn) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['offers', index, 'linkedIpn'],
                    message: 'Offer belongs to another IPN',
                });
            }
        }
        // Validate provenance, not the choice: old reports must retain their saved decisions.
        for (const [priceBreak, selection] of Object.entries(snapshot.selectionsByBreak)) {
            if (selection === null) continue;
            const exists = snapshot.offers.some(
                (offer) =>
                    offer.id === selection.offerId &&
                    offer.priceBreaks.some(
                        (price) => price.quantity === selection.quantity && price.unitPrice === selection.unitPrice,
                    ),
            );
            if (!exists) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['selectionsByBreak', priceBreak],
                    message: 'Selection has no matching source offer price',
                });
            }
        }
    });
/** Explicit version dispatch: future wire versions must keep the V1 decoder intact. */
export const MarketSavingsStandardPriceSnapshotSchema = z.discriminatedUnion('version', [
    MarketSavingsStandardPriceSnapshotV1Schema,
]);
export type MarketSavingsStandardPriceSnapshot = z.infer<typeof MarketSavingsStandardPriceSnapshotSchema>;
export type MarketSavingsStandardPriceSnapshotOffer = z.infer<typeof OfferV1Schema>;
export type MarketSavingsStandardPriceSnapshotSelection = z.infer<typeof SelectionV1Schema>;

// Intentionally frozen with V1, rather than coupled to future canonical-break changes.
const V1_BREAKS = [
    ['1', 1],
    ['100', 100],
    ['1k', 1_000],
    ['10k', 10_000],
    ['50k', 50_000],
] as const;

type StandardPriceCandidate = {
    offer: MarketSavingsStandardPriceSnapshotOffer;
    priceBreak: MarketSavingsStandardPriceSnapshotOffer['priceBreaks'][number];
};

// Compare UTC calendar days, ignoring time of day. Missing dates sort last.
function utcDay(timestamp: string | null): number {
    return timestamp === null ? -Infinity : Math.floor(Date.parse(timestamp) / 86_400_000);
}

const byDay = ({ offer }: StandardPriceCandidate) => utcDay(offer.observedAt);
const byKind = ({ offer }: StandardPriceCandidate) => (offer.linkedIpn === undefined ? 1 : 0);
const byPrice = ({ priceBreak }: StandardPriceCandidate) => priceBreak.unitPrice;
// Stable evidence selection when all business rules tie.
const byId = ({ offer }: StandardPriceCandidate) => offer.id;

function selectPriceBreak(offer: MarketSavingsStandardPriceSnapshotOffer, bucketQuantity: number) {
    return maxBy(
        offer.priceBreaks.filter(({ quantity }) => quantity <= bucketQuantity),
        'quantity',
    );
}

/** Caller must scope part-owned offers to approved parts for this IPN. Other-IPN offers are excluded. */
export function createMarketSavingsStandardPriceSnapshot({
    ipn,
    capturedAt,
    currency,
    offers,
}: {
    ipn: string;
    capturedAt: string;
    currency: string;
    offers: readonly MarketSavingsStandardPriceSnapshotOffer[];
}): MarketSavingsStandardPriceSnapshot {
    const ownedOffers = offers.filter(
        (offer) => offer.linkedIpn === ipn || (offer.linkedIpn === undefined && offer.partId !== undefined),
    );
    const select = (demand: number): MarketSavingsStandardPriceSnapshotSelection | null => {
        const candidates = ownedOffers.flatMap((offer) => {
            const priceBreak = selectPriceBreak(offer, demand);
            return priceBreak ? [{ offer, priceBreak }] : [];
        });
        const selected = orderBy(candidates, [byDay, byKind, byPrice, byId], ['desc', 'asc', 'asc', 'asc'])[0];
        return selected ? { offerId: selected.offer.id, ...selected.priceBreak } : null;
    };
    return MarketSavingsStandardPriceSnapshotV1Schema.parse({
        version: 1,
        ipn,
        capturedAt,
        currency,
        offers: ownedOffers,
        selectionsByBreak: {
            '1': select(1),
            '100': select(100),
            '1k': select(1_000),
            '10k': select(10_000),
            '50k': select(50_000),
        },
    });
}

/** Read saved decisions only; never run the current selection algorithm on an old report. */
export function getStandardPriceSnapshotSelection(
    snapshot: MarketSavingsStandardPriceSnapshot,
    demandQuantity: number | null,
): MarketSavingsStandardPriceSnapshotSelection | null {
    if (demandQuantity === null || !Number.isFinite(demandQuantity)) return null;
    const tier = V1_BREAKS.findLast(([, quantity]) => quantity <= demandQuantity);
    return tier ? snapshot.selectionsByBreak[tier[0]] : null;
}
